import { useEffect, useMemo, useState } from "react";
import type { TravelMode } from "@poi-planner/shared";
import type { TravelSegment } from "../utils/travel";
import { estimateTravel } from "../utils/geo";
import {
  peekTravelRoute,
  requestTravelRoute,
  type TravelRouteQuery,
} from "../services/travelService";

export interface ResolvedTravelSegment extends TravelSegment {
  /** Mode actually shown — auto transit falls back to drive when unroutable */
  resolvedMode: TravelMode;
  travelMinutes: number;
  distanceKm: number;
  /** "routes" once Google answered; "estimate" while loading or unavailable */
  source: "routes" | "estimate";
}

type RouteOutcome = {
  mode: TravelMode;
  durationMinutes: number;
  distanceMeters: number;
};

function queryFor(segment: TravelSegment, mode: TravelMode): TravelRouteQuery {
  return {
    origin: segment.from.coords,
    destination: segment.to.coords,
    mode,
    departureTime: segment.departureTime,
  };
}

function toOutcome(
  mode: TravelMode,
  result: ReturnType<typeof peekTravelRoute>,
): RouteOutcome | null {
  if (result?.status !== "ok" || result.durationMinutes == null) return null;
  return {
    mode,
    durationMinutes: result.durationMinutes,
    distanceMeters: result.distanceMeters ?? 0,
  };
}

/** Automatic transit that Google can't route is retried as a drive. */
function fallbackMode(segment: TravelSegment): TravelMode | null {
  return segment.modeSource === "auto" && segment.mode === "transit"
    ? "drive"
    : null;
}

function peekOutcome(segment: TravelSegment): RouteOutcome | null {
  const primary = peekTravelRoute(queryFor(segment, segment.mode));
  const outcome = toOutcome(segment.mode, primary);
  if (outcome || primary?.status !== "unavailable") return outcome;
  const fallback = fallbackMode(segment);
  return fallback
    ? toOutcome(fallback, peekTravelRoute(queryFor(segment, fallback)))
    : null;
}

async function resolveOutcome(
  segment: TravelSegment,
): Promise<RouteOutcome | null> {
  const primary = await requestTravelRoute(queryFor(segment, segment.mode));
  const outcome = toOutcome(segment.mode, primary);
  if (outcome) return outcome;
  const fallback = fallbackMode(segment);
  if (!fallback) return null;
  return toOutcome(
    fallback,
    await requestTravelRoute(queryFor(segment, fallback)),
  );
}

function resolveSegment(
  segment: TravelSegment,
  outcome: RouteOutcome | null | undefined,
): ResolvedTravelSegment {
  if (!outcome) {
    return {
      ...segment,
      resolvedMode: segment.mode,
      travelMinutes: segment.estimate.travelMinutes,
      distanceKm: segment.estimate.distanceKm,
      source: "estimate",
    };
  }
  const distanceKm =
    outcome.distanceMeters > 0
      ? outcome.distanceMeters / 1000
      : segment.estimate.distanceKm;
  return {
    ...segment,
    // A fallback drive shows the drive icon, not the transit it replaced
    estimate:
      outcome.mode === segment.mode
        ? segment.estimate
        : (estimateTravel(
            segment.from.coords,
            segment.to.coords,
            outcome.mode,
          ) ?? segment.estimate),
    resolvedMode: outcome.mode,
    travelMinutes: outcome.durationMinutes,
    distanceKm,
    source: "routes",
    overrun: outcome.durationMinutes > segment.gapMinutes,
  };
}

/**
 * Upgrades a day's offline travel estimates to Google Routes durations.
 * Segments render immediately from the estimate (or a session-cached route)
 * and update in place as lookups resolve.
 */
export function useResolvedTravel(
  segments: TravelSegment[],
): ResolvedTravelSegment[] {
  const [outcomes, setOutcomes] = useState<
    ReadonlyMap<string, RouteOutcome | null>
  >(() => new Map());

  // `segments` is memoized by the caller on the schedule, so drag-preview
  // ticks don't re-run this; a re-run for unchanged pins resolves from the
  // session cache without a request.
  useEffect(() => {
    let cancelled = false;
    for (const segment of segments) {
      const cacheId = outcomeId(segment);
      void resolveOutcome(segment).then((outcome) => {
        if (cancelled) return;
        setOutcomes((current) => {
          const previous = current.get(cacheId);
          if (
            current.has(cacheId) &&
            previous?.mode === outcome?.mode &&
            previous?.durationMinutes === outcome?.durationMinutes &&
            previous?.distanceMeters === outcome?.distanceMeters
          ) {
            return current;
          }
          const next = new Map(current);
          next.set(cacheId, outcome);
          return next;
        });
      });
    }
    return () => {
      cancelled = true;
    };
  }, [segments]);

  return useMemo(
    () =>
      segments.map((segment) => {
        const id = outcomeId(segment);
        const outcome = outcomes.has(id)
          ? outcomes.get(id)
          : peekOutcome(segment);
        return resolveSegment(segment, outcome);
      }),
    [segments, outcomes],
  );
}

/** A segment's lookup identity: its pin, endpoints, mode, and departure. */
function outcomeId(segment: TravelSegment): string {
  const { from, to } = segment;
  return [
    segment.segmentKey,
    segment.mode,
    segment.modeSource,
    segment.departureTime,
    `${from.coords.lat},${from.coords.lng}`,
    `${to.coords.lat},${to.coords.lng}`,
  ].join("|");
}
